import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { authAPI } from '../../api';
import { getClientAreaSignInPath } from '../../lib/authFailureRedirect';
import { Loader2 } from 'lucide-react';

/**
 * Portal entry: exchange invoice public link token for JWT and redirect to client dashboard.
 * Used when client clicks "Go to Client Portal" on the invoice HTML preview.
 */
export default function PortalEntry() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setErrorMessage('Missing link. Please use the link from your invoice email.');
      return;
    }

    let cancelled = false;

    authAPI
      .loginWithPortalToken(token)
      .then((data) => {
        if (cancelled) return;
        login(data.user, data.token);
        setStatus('success');
        if (data.needsOnboarding) {
          navigate('/client/onboarding', { replace: true });
        } else {
          navigate('/client/dashboard', { replace: true });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus('error');
        setErrorMessage(err.message || 'This link is invalid or has expired. Please log in with your email and password.');
      });

    return () => {
      cancelled = true;
    };
  }, [searchParams, login, navigate]);

  if (status === 'error') {
    const slugHint =
      searchParams.get('portalSlug')?.trim() ||
      searchParams.get('slug')?.trim() ||
      '';
    const signInPath = getClientAreaSignInPath(slugHint || undefined);
    const signInLabel =
      signInPath === '/login' ? 'Staff sign in' : 'Sign in to client portal';
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full text-center space-y-4">
          <p className="text-gray-700 dark:text-gray-300">{errorMessage}</p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center items-center">
            <button
              type="button"
              onClick={() => navigate(signInPath)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {signInLabel}
            </button>
            {signInPath !== '/login' && (
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-sm text-gray-600 dark:text-gray-400 hover:underline"
              >
                Staff sign in
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="w-10 h-10 text-blue-600 animate-spin" aria-hidden />
        <p className="text-gray-700 dark:text-gray-300">Signing you in to your portal…</p>
      </div>
    </div>
  );
}
